import { normalizeAbilitySlug } from '../data/abilityPapers';

/**
 * EDVise pastel palette — one theme per core ability (flat tile + icon colours).
 * Ink colours follow styles/theme.css so they stay readable in light and dark mode.
 * @typedef {{ tileBg: string, surface: string, ink: string, inkMuted: string, titleClass: string, hoverBorder: string }} AbilityTheme
 */

/** Neutral panel fill — follows styles/theme.css light/dark tokens. */
const THEME_SURFACE = 'rgb(var(--edvise-surface))';

/**
 * @param {'qr' | 'lr' | 'ct' | 'vr' | 'cm'} key
 */
function abilityInk(key) {
  return {
    ink: `rgb(var(--ability-${key}-ink))`,
    inkMuted: `rgb(var(--ability-${key}-ink-muted))`,
  };
}

/** @type {Record<string, AbilityTheme>} */
export const ABILITY_PASTEL_THEMES = {
  'quantitative-reasoning': {
    tileBg: '#A0C4FF',
    surface: THEME_SURFACE,
    ...abilityInk('qr'),
    titleClass: 'text-blue-900 dark:text-blue-200',
    hoverBorder: 'hover:border-blue-300 dark:hover:border-blue-600'
  },
  'logical-reasoning': {
    tileBg: '#B5EAD7',
    surface: THEME_SURFACE,
    ...abilityInk('lr'),
    titleClass: 'text-emerald-900 dark:text-emerald-200',
    hoverBorder: 'hover:border-emerald-300 dark:hover:border-emerald-600'
  },
  'critical-thinking': {
    tileBg: '#D4C1EC',
    surface: THEME_SURFACE,
    ...abilityInk('ct'),
    titleClass: 'text-violet-900 dark:text-violet-200',
    hoverBorder: 'hover:border-violet-300 dark:hover:border-violet-600'
  },
  'verbal-reasoning': {
    tileBg: '#FFD8BE',
    surface: THEME_SURFACE,
    ...abilityInk('vr'),
    titleClass: 'text-amber-900 dark:text-amber-200',
    hoverBorder: 'hover:border-amber-300 dark:hover:border-amber-600'
  },
  communication: {
    tileBg: '#FFB7C5',
    surface: THEME_SURFACE,
    ...abilityInk('cm'),
    titleClass: 'text-rose-900 dark:text-rose-200',
    hoverBorder: 'hover:border-rose-300 dark:hover:border-rose-600'
  }
};

const DEFAULT_THEME = ABILITY_PASTEL_THEMES['quantitative-reasoning'];

/**
 * @param {string | null | undefined} slug
 * @returns {AbilityTheme}
 */
export function getAbilityTheme(slug) {
  return ABILITY_PASTEL_THEMES[normalizeAbilitySlug(slug || '')] || DEFAULT_THEME;
}

const VALUE_BAR_COLOR_WEIGHTS = [0.18, 0.32, 0.46, 0.6, 0.74];

function isDarkMode() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

function mixHexWithNeutral(hex, colorWeight) {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  // Light mode: blend toward white. Dark mode: blend toward surface so bars sit on dark tiles.
  const nr = isDarkMode() ? 39 : 255;
  const ng = isDarkMode() ? 39 : 255;
  const nb = isDarkMode() ? 42 : 255;
  const mix = (channel, neutral) => Math.round(channel * colorWeight + neutral * (1 - colorWeight));
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(r, nr))}${toHex(mix(g, ng))}${toHex(mix(b, nb))}`;
}

/**
 * Ink for labels/scores on ability tiles (theme-aware via CSS variables).
 * @param {string | null | undefined} slug
 * @returns {string}
 */
export function getAbilityContrastInk(slug) {
  return getAbilityTheme(slug).ink;
}

/**
 * Five gentle fill shades for ability score value bars.
 * Recalculate when theme changes (callers should depend on `isDark`).
 * @param {string | null | undefined} slug
 * @returns {string[]}
 */
export function getAbilityValueBarShades(slug) {
  const { tileBg } = getAbilityTheme(slug);
  return VALUE_BAR_COLOR_WEIGHTS.map((weight) => mixHexWithNeutral(tileBg, weight));
}
